import { Capacitor, registerPlugin } from "@capacitor/core";
import { runNativePrompt } from "@/lib/nativePrompts";

// Apple Declared Age Range (iOS 26+) — façade du plugin natif ios/App/App/DeclaredAgeRangePlugin.swift.
// Yalola ne reçoit qu'une TRANCHE (under13 · 13-15 · 16-17 · 18+), jamais une date de naissance. La règle
// Yalola existante (date de naissance, min_registration_age = 13) reste le mécanisme de base partout ;
// ici on la complète sur iOS : < 13 → mur ; REFUS explicite → mur seulement là où l'Age Assurance est exigée
// par la loi (isEligibleForAgeFeatures / declaredAgeRangeRequired), sinon repli sur la règle Yalola.
// « unavailable » (Simulateur — l'API répond toujours notAvailable —, pas de compte Apple, API en erreur) n'est
// PAS un refus : jamais de mur ni de déconnexion, la règle Yalola s'applique (recommandation Apple : fallback).
export const AGE_GATES = [13, 16, 18];
export const AGE_RANGE_KEY = "yalola.age_range";
export const AGE_RANGE_TTL_MS = 30 * 24 * 3600 * 1000;      // réponse partagée : re-lue au plus tous les 30 j
export const AGE_RANGE_RETRY_MS = 24 * 3600 * 1000;         // refus / indisponible : on ne redemande pas avant 24 h
export const AGE_BLOCK_EVENT = "yalola:age-block";
export const AGE_RANGE_EVENT = "yalola:age-range";

const YalolaAgeRange = registerPlugin("YalolaAgeRange");

export const isIosNative = () => Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";

// Miroir du calcul natif (lowerBound nil → sous la 1re barrière).
export const bandFromBounds = (lower, gates = AGE_GATES) => {
  if (lower == null) return `under${gates[0]}`;
  if (gates.length > 2 && lower >= gates[2]) return `${gates[2]}+`;
  if (gates.length > 1 && lower >= gates[1]) return `${gates[1]}-${(gates.length > 2 ? gates[2] : lower + 2) - 1}`;
  return `${gates[0]}-${(gates.length > 1 ? gates[1] : lower + 2) - 1}`;
};
export const isUnderFirstGate = (band) => /^under/.test(String(band || ""));

// Verdict Yalola : allow · fallback (règle date de naissance seule) · block_under13 · block_required.
export const ageVerdict = ({ status, band, eligible, required } = {}) => {
  if (status === "sharing") return isUnderFirstGate(band) ? "block_under13" : "allow";
  if (status === "declined") return required === true || eligible === true ? "block_required" : "fallback";
  return "fallback";   // unsupported · unavailable · inconnu
};
export const isBlocking = (verdict) => verdict === "block_under13" || verdict === "block_required";

export const readAgeRange = (storage = globalThis.localStorage) => {
  try { const raw = storage?.getItem(AGE_RANGE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
export const writeAgeRange = (rec, storage = globalThis.localStorage) => {
  try { storage?.setItem(AGE_RANGE_KEY, JSON.stringify(rec)); } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent(AGE_RANGE_EVENT, { detail: rec })); } catch { /* noop */ }
  return rec;
};
export const clearAgeRange = (storage = globalThis.localStorage) => { try { storage?.removeItem(AGE_RANGE_KEY); } catch { /* noop */ } };

export const isFresh = (rec, now = Date.now()) => {
  if (!rec || !rec.at) return false;
  const ttl = rec.status === "sharing" ? AGE_RANGE_TTL_MS : AGE_RANGE_RETRY_MS;
  return now - rec.at < ttl;
};

let inFlight = null;
// Lecture de la tranche (feuille système iOS la 1re fois, puis cache Apple + cache local). `force` ignore le cache local.
export async function checkAgeRange({ force = false } = {}) {
  if (!isIosNative()) return { status: "unsupported", band: null, verdict: "fallback", cached: false };
  const cached = readAgeRange();
  if (!force && isFresh(cached)) return { ...cached, verdict: ageVerdict(cached), cached: true };
  if (inFlight) return inFlight;
  inFlight = (async () => {
    let res;
    try { res = await runNativePrompt("age", () => YalolaAgeRange.requestAgeRange({ gates: AGE_GATES })); }
    catch (e) { res = { status: "unavailable", error: String(e?.message || e) }; }
    res = res || { status: "unavailable" };
    let reg = { eligible: null, required: null, features: [] };
    if (res.status !== "unsupported") {
      try { reg = { ...reg, ...(await YalolaAgeRange.regulatoryStatus()) }; } catch { /* noop */ }
    }
    const rec = {
      status: res.status || "unavailable",
      band: res.status === "sharing" ? (res.band || bandFromBounds(res.lowerBound)) : null,
      lowerBound: res.lowerBound ?? null,
      upperBound: res.upperBound ?? null,
      declaration: res.declaration || null,
      parentalControls: Array.isArray(res.parentalControls) ? res.parentalControls : [],
      error: res.error || null,
      eligible: typeof reg.eligible === "boolean" ? reg.eligible : null,
      required: typeof reg.required === "boolean" ? reg.required : null,
      features: Array.isArray(reg.features) ? reg.features : [],
      sentFor: cached?.band === (res.band || null) ? cached?.sentFor || null : null,
      at: Date.now(),
    };
    writeAgeRange(rec);
    return { ...rec, verdict: ageVerdict(rec), cached: false };
  })();
  try { return await inFlight; } finally { inFlight = null; }
}

// Charge utile envoyée au serveur (inscription / POST /auth/age-assurance) : la bande, jamais de date.
export const ageAssurancePayload = (rec) => {
  if (!rec || rec.status !== "sharing" || !rec.band) return null;
  return {
    provider: "apple_declared_age_range",
    band: rec.band,
    declaration: rec.declaration || null,
    parental_controls: rec.parentalControls || [],
    gates: AGE_GATES,
    os: "ios",
    eligible: rec.eligible ?? null,
  };
};
export const markAgeRangeSent = (userId) => {
  const rec = readAgeRange();
  if (rec) writeAgeRange({ ...rec, sentFor: userId });
};

// État « mur » partagé (Onboarding + AgeAssuranceGate) : { verdict, rec } ou null.
let block = null;
export const getAgeBlock = () => block;
export const setAgeBlock = (next) => {
  block = next || null;
  try { window.dispatchEvent(new CustomEvent(AGE_BLOCK_EVENT, { detail: block })); } catch { /* noop */ }
};
